import { iconSize, semanticColors } from "../../tokens";
import type { TypographyProps } from "../typography";
import type { TagRibbonEmphasis, TagRibbonPosition, TagRibbonSize, TagRibbonState } from "./types";

type RibbonTextColor = NonNullable<TypographyProps["color"]>;

export const tagRibbonTextColor: Record<
  TagRibbonEmphasis,
  Record<TagRibbonState, RibbonTextColor>
> = {
  solid: {
    primary: "brand-on-primary",
    success: "system-success-on-success",
    danger: "system-danger-on-danger",
    warning: "system-warning-on-warning",
  },
  subtle: {
    primary: "brand-on-primary-surface",
    success: "system-success-on-surface",
    danger: "system-danger-on-surface",
    warning: "system-warning-on-surface",
  },
};

export const tagRibbonBackgroundClass: Record<TagRibbonEmphasis, Record<TagRibbonState, string>> = {
  solid: {
    primary: "bg-luce-brand-primary",
    success: "bg-luce-system-success-default",
    danger: "bg-luce-system-danger-default",
    warning: "bg-luce-system-warning-default",
  },
  subtle: {
    primary: "bg-luce-brand-primarySurface",
    success: "bg-luce-system-success-surface",
    danger: "bg-luce-system-danger-surface",
    warning: "bg-luce-system-warning-surface",
  },
};

// Figma's labels: small `body/sm` Medium (12/16, desktop 14/20), medium
// `body/md` Regular (14/20, desktop 16/24)
export const tagRibbonTypography = {
  sm: "body-sm-medium",
  md: "body-md",
} as const;

// Figma's StarFour frame is 12 / 16 in both modes, so no desktop step
export const tagRibbonIconSize: Record<TagRibbonSize, number> = {
  sm: iconSize.xs,
  md: iconSize.sm,
};

// The two corners Figma rounds, the outer top and the inner bottom, bound to
// `_tag-ribbon/radius` (12 in both modes): the `radius-lg` token
export const tagRibbonCornerClass: Record<TagRibbonPosition, string> = {
  left: "rounded-br-radius-lg rounded-tl-radius-lg",
  right: "rounded-bl-radius-lg rounded-tr-radius-lg",
};

// Subtle only: Figma draws no border on Filled
export const tagRibbonBorderClass: Record<TagRibbonState, string> = {
  primary: "border-luce-brand-primaryBorder",
  success: "border-luce-system-success-border",
  danger: "border-luce-system-danger-border",
  warning: "border-luce-system-warning-border",
};

// Figma's fold tail (`bg`), a wedge under the outer end. Small draws it at half
// the width, so the path scales horizontally only.
export const tagRibbonTailPath: Record<TagRibbonPosition, string> = {
  left: "M0 0H16C7.11111 0.571429 1.77778 1.71429 0 4V0Z",
  right: "M16 0H0C8.88889 0.571429 14.2222 1.71429 16 4V0Z",
};

export const tagRibbonTailWidth: Record<TagRibbonSize, number> = {
  sm: 8,
  md: 16,
};

export const tagRibbonTailHeight = 4;

// The state's on-surface colour, on both emphases
export const tagRibbonTailColor: Record<TagRibbonState, string> = {
  primary: semanticColors.brand.onPrimarySurface,
  success: semanticColors.system.success.onSurface,
  danger: semanticColors.system.danger.onSurface,
  warning: semanticColors.system.warning.onSurface,
};
